// @flow
import React, {useState, useEffect} from 'react';
import {
  Grid,
  Typography,
  Divider,
  InputAdornment,
  makeStyles,
  Icon,
  Snackbar,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import {useParams, useHistory} from 'react-router-dom';
import AddIcon from '@material-ui/icons/Add';
import {useForm, useFieldArray} from 'react-hook-form';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../../assets/styles/colors';
import {useStoreCountry} from '../../../../country/utils';
import {useGetCurrencyCountry} from '../../logistic-pickup/utils';
import LocationSelector from './components/LocationSelector';
import {
  useCRUDAdvancedRate,
  useFetchShippingAdvanced,
  useFetchShippingMethods,
  useGetShippingMethodActive,
  useResetState,
  useSaveLocationsSelected,
  useUpdateShippingMethodActive,
} from '../utils';
import ActivateShippingModal from '../components/ActivateShippingModal';
import CustomExitPrompt from '../../../../../elements/CustomExitPrompt/CustomExitPrompt';
import {removeEmptyObjects} from './components/LocationSelector/utils';

const useStyles = makeStyles(theme => ({
  containerGrid: {
    display: 'flex',
    justifyContent: 'space-between',
    columnGap: theme.spacing(3),
    backgroundColor: 'white',
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
    border: '1px solid #dbdbe0',
    borderTop: 'none',
    paddingInline: theme.spacing(2),
  },
  containerGridSteps: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    width: '100%',
  },
  containerSteps: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
  },
  subtitleText: {
    color: colors.grey900,
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '22px',
    paddingBlock: theme.spacing(2),
  },
  subtitleText2: {
    color: colors.grey900,
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '22px',
  },
  textButton: {
    color: colors.grey800,
    fontSize: '14px',
  },
  messageField: {
    margin: theme.spacing(1, 0),
  },
  greyTextSmall: {
    color: '#717190!important',
    fontSize: '0.75rem!important',
  },
  greyTextSmallContainer: {
    color: '#717190!important',
    fontSize: '0.75rem!important',
    maxHeight: '100px',
    overflow: 'auto',
  },
  messageTips: {
    margin: theme.spacing(1, 0),
  },
  bold: {
    fontWeight: 600,
  },
  divider: {
    background: colors.grey300,
    width: '100%',
    marginBottom: '1rem',
  },
  dividerNoMargin: {
    background: colors.grey300,
    width: '100%',
  },
  messageTipsRow: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  messageRow: {
    display: 'flex',
  },
  summaryContainerGap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxWidth: '300px',
    minWidth: '300px',
  },
  summaryContainer: {
    backgroundColor: colors.grey200,
    padding: theme.spacing(2),
    borderRadius: '8px',
    height: 'fit-content',
    marginTop: theme.spacing(2),
  },
  newRateCard: {
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    rowGap: theme.spacing(1),
  },
  newRateButton: {
    border: '1px dashed #D4D4DD',
    borderRadius: '8px',
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-start',
    width: '50%',
    marginTop: theme.spacing(2),
  },
  wrapperAddBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  iconAdd: {
    color: colors.grey700,
  },
  deleteRateButton: {
    width: '100%',
    backgroundColor: '#F44336',
    color: 'white',
    '&:hover': {
      backgroundColor: '#F44336',
    },
  },
  submitButton: {
    marginBottom: '32px',
  },
}));

export default function LogisticsShippingNewAdvancedInternational() {
  const classes = useStyles();
  const history = useHistory();
  const {adminRateId} = useParams();
  const {
    register,
    getValues,
    handleSubmit,
    errors,
    watch,
    control,
    formState,
  } = useForm();
  const {isDirty} = formState;
  const {remove} = useFieldArray({
    control,
    name: 'shippingRates',
  });
  const [newRates, setNewRates] = useState([]);
  const [alertErrorComplete, setAlertErrorComplete] = useState(false);
  const [alertErrorRate, setAlertErrorRate] = useState(false);
  const [alertErrorCountry, setAlertErrorCountry] = useState(false);
  const [locationsSelected, setLocationsSelected] = useState({});
  const [showModalActive, setShowModalActive] = useState(false);
  const [optionSave, setOptionSave] = useState('');
  const shippingRatesWatch = watch('shippingRates');

  const {shippinglocationsSelected} = useSaveLocationsSelected();
  const {availableShippingCountries} = useFetchShippingMethods();
  const {
    fetchShippingAdvancedData,
    shippingAdvanced,
  } = useFetchShippingAdvanced();
  const {
    isSaving,
    savingAdvancedRateSuccess,
    createAdvancedRate,
    updateAdvancedRate,
  } = useCRUDAdvancedRate();
  const country = useStoreCountry();
  const currency = useGetCurrencyCountry();
  const {updateShippingMethodActive} = useUpdateShippingMethodActive();
  const shippingMethod = useGetShippingMethodActive();
  const {resetState} = useResetState();

  const limitNewRates = 3;

  const handleValidation = () => {
    if (
      shippingMethod.shippingMethodActive.storeShippingMethodSettings
        .storeShippingMethodType !== 'ADVANCED'
    ) {
      setShowModalActive(true);
      return false;
    }
    return true;
  };

  const handleBeforeSubmit = () => {
    if (!handleValidation()) {
      return;
    }

    handleFormSubmit('save');
  };

  const handleFormSubmit = (optionSave: string) => {
    const data = getValues();
    const {description, label} = data;
    const selectedLocations = shippinglocationsSelected || {};
    const selectedLocationsClear = removeEmptyObjects(selectedLocations);

    const selectedLocationsLength = Object.keys(selectedLocationsClear).length;

    if (selectedLocationsLength === 0) {
      setAlertErrorComplete(true);
      return;
    }

    if (selectedLocationsLength > 1) {
      setAlertErrorCountry(true);
      return;
    }

    if (newRates.length === 0 || !shippingRatesWatch) {
      setAlertErrorRate(true);
      return;
    }

    const updatedShippingRates = shippingRatesWatch.map(({id, ...rest}) => ({
      ...rest,
      enabled: true,
    }));

    const advancedRate = {
      description,
      label,
      location: selectedLocationsClear,
      shippingRates: updatedShippingRates,
    };

    if (adminRateId) {
      const location =
        selectedLocations && Object.keys(selectedLocations).length > 0
          ? selectedLocationsClear
          : undefined;

      const formatAdvancedRate = {
        advancedRateId: adminRateId,
        advancedRate: {
          ...advancedRate,
          location,
        },
      };

      updateAdvancedRate(formatAdvancedRate);
    } else {
      createAdvancedRate(advancedRate);
    }

    if (optionSave === 'saveAndActivate') {
      setOptionSave(optionSave);
    }
  };

  useEffect(() => {
    if (adminRateId) {
      fetchShippingAdvancedData(adminRateId);
    }
  }, [adminRateId, fetchShippingAdvancedData]);

  useEffect(() => {
    if (savingAdvancedRateSuccess) {
      if (optionSave === 'saveAndActivate') {
        updateShippingMethodActive({
          shippingMethodType: 'ADVANCED',
        });
      }
      history.push('/configure/logistics/shipping/advanced');
    }
  }, [
    savingAdvancedRateSuccess,
    history,
    updateShippingMethodActive,
    optionSave,
  ]);

  useEffect(() => {
    setNewRates(
      shippingAdvanced && shippingAdvanced.shippingRates
        ? shippingAdvanced.shippingRates
        : []
    );
  }, [shippingAdvanced]);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  return (
    <>
      <CustomExitPrompt when={isDirty && !savingAdvancedRateSuccess} />
      <Snackbar
        open={alertErrorComplete}
        autoHideDuration={4000}
        onClose={() => setAlertErrorComplete(false)}
      >
        <Alert severity="error">
          Seleccione al menos una localidad para continuar
        </Alert>
      </Snackbar>
      <Snackbar
        open={alertErrorCountry}
        autoHideDuration={4000}
        onClose={() => setAlertErrorCountry(false)}
      >
        <Alert severity="error">
          Solo puede seleccionar localidades de un solo país
        </Alert>
      </Snackbar>
      <Snackbar
        open={alertErrorRate}
        autoHideDuration={4000}
        onClose={() => setAlertErrorRate(false)}
      >
        <Alert severity="error">
          Debe configurar almenos una tarifa para continuar
        </Alert>
      </Snackbar>
      {!adminRateId && (
        <form
          className={classes.containerGrid}
          onSubmit={handleSubmit(handleBeforeSubmit)}
        >
          <Grid container spacing={1} className={classes.containerGridSteps}>
            <Grid item xs={12} md={12}>
              <Typography className={classes.subtitleText}>
                1. Título de Zona de Reparto
              </Typography>
              <CustomTextField
                name="label"
                label="Título"
                error={!!errors.label}
                helperText={errors.label ? errors.label.message : ''}
                type="text"
                inputRef={register({required: 'Campo obligatorio'})}
                disabled={isSaving}
              />
            </Grid>
            {/* <Grid item xs={12} md={12}>
          <Typography className={classes.subtitleText}>
            2. Pedido mínimo para delivery
          </Typography>
          <CustomTextField
            name="price"
            label="Precio de pedido"
            defaultValue={adminRate ? adminRate.price : ''}
            InputProps={{
              className: classes.textInput,
              startAdornment: (
                <InputAdornment position="start">
                  {currency ? currency.symbol : ''}
                </InputAdornment>
              ),
            }}
            inputRef={register({
              required: 'Campo obligatorio',
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: 'Ingrese un monto válido',
              },
            })}
            error={!!errors.price}
            helperText={errors.price ? errors.price.message : ''}
            // disabled={isSaving }
          />
        </Grid> */}
            <Divider className={classes.dividerNoMargin} />
            <Grid item xs={12} md={12}>
              <Typography className={classes.subtitleText}>
                2. Crear tarifa(s)
              </Typography>
              <Grid container spacing={3}>
                {newRates.map((shippingRate, index) => (
                  <Grid item xs={12} md={6} key={shippingRate.id}>
                    <div className={classes.newRateCard}>
                      <CustomTextField
                        name={`shippingRates[${index}].name`}
                        label="Nombre de tarifa"
                        inputRef={register({required: 'Campo obligatorio'})}
                        defaultValue={shippingRate ? shippingRate.name : ''}
                        error={
                          !!errors.shippingRates &&
                          !!errors.shippingRates[index] &&
                          !!errors.shippingRates[index].name
                        }
                        helperText={
                          errors.shippingRates && errors.shippingRates[index]
                            ? errors.shippingRates[index].message
                            : ''
                        }
                        disabled={isSaving}
                        type="text"
                      />
                      <CustomTextField
                        name={`shippingRates[${index}].price`}
                        label="Precio de pedido"
                        InputProps={{
                          className: classes.textInput,
                          startAdornment: (
                            <InputAdornment position="start">
                              {currency ? currency.symbol : ''}
                            </InputAdornment>
                          ),
                        }}
                        inputRef={register({
                          required: 'Campo obligatorio',
                        })}
                        defaultValue={shippingRate ? shippingRate.price : ''}
                        type="number"
                        inputProps={{step: '.01'}}
                        error={
                          !!errors.shippingRates &&
                          !!errors.shippingRates[index] &&
                          !!errors.shippingRates[index].price
                        }
                        helperText={
                          errors.shippingRates && errors.shippingRates[index]
                            ? errors.shippingRates[index].message
                            : ''
                        }
                        disabled={isSaving}
                      />
                      <CustomTextField
                        name={`shippingRates[${index}].description`}
                        label="Descripción (Opcional)"
                        inputRef={register}
                        disabled={isSaving}
                        defaultValue={
                          shippingRate ? shippingRate.description : ''
                        }
                      />
                      <CustomButton
                        fullWidth
                        className={classes.deleteRateButton}
                        onClick={() => {
                          setNewRates(pre =>
                            pre.filter(rate => rate.id !== shippingRate.id)
                          );
                        }}
                      >
                        Eliminar tarifa
                      </CustomButton>
                    </div>
                  </Grid>
                ))}
              </Grid>
              {limitNewRates > newRates.length && (
                <CustomButton
                  fullWidth
                  className={classes.newRateButton}
                  onClick={() =>
                    setNewRates(
                      newRates.concat({
                        id: newRates.length + 1,
                        name: '',
                        price: '',
                        description: '',
                      })
                    )
                  }
                  // disabled={hasShippingMethodFeatureDisabled}
                >
                  <div className={classes.wrapperAddBtn}>
                    <Icon size="small">
                      <AddIcon className={classes.iconAdd} />
                    </Icon>
                    <Typography className={classes.textButton}>
                      Agregar nueva
                    </Typography>
                  </div>
                </CustomButton>
              )}
            </Grid>
            <Divider className={classes.dividerNoMargin} />
            <Grid item xs={12} md={12}>
              <Typography className={classes.subtitleText}>
                3. Elegir localidades
              </Typography>
              {availableShippingCountries &&
                availableShippingCountries.length > 0 &&
                availableShippingCountries
                  .filter(countryI => countryI.code !== country.code)
                  .map(country => (
                    <LocationSelector
                      key={`country-${country.code}`}
                      country={country}
                      setLocationsSelected={selected => {
                        setLocationsSelected(prevState => ({
                          ...prevState,
                          [country.code]: selected,
                        }));
                      }}
                      locationsSelected={locationsSelected[country.code] || []}
                    />
                  ))}
            </Grid>
            <Divider className={classes.dividerNoMargin} />
            <Grid item xs={12} md={12}>
              <Typography className={classes.subtitleText}>
                4. Mensaje de envio
              </Typography>
              <CustomTextField
                multiline
                rows={5}
                rowsMax={18}
                label="Mensaje"
                className={classes.messageField}
                InputProps={{
                  className: classes.textInput,
                }}
                InputLabelProps={{className: classes.textInputLabel}}
                name="description"
                error={!!errors.description}
                helperText={errors.description ? errors.description : ''}
                type="text"
                inputRef={register}
                disabled={isSaving}
              />
              <div className={classes.messageTips}>
                <Typography className={classes.greyTextSmall} paragraph>
                  Es importante incluir información como:
                </Typography>
                <div className={classes.messageTipsRow}>
                  <div>
                    <Typography
                      className={`${classes.greyTextSmall} ${classes.bold}`}
                    >
                      Días de despacho
                    </Typography>
                    <Typography className={classes.greyTextSmall} paragraph>
                      Despachos los martes y jueves
                    </Typography>
                  </div>
                  <Divider orientation="vertical" flexItem />
                  <div>
                    <Typography
                      className={`${classes.greyTextSmall} ${classes.bold}`}
                    >
                      Rango de tiempo hasta la recepción del pedido:
                    </Typography>
                    <div className={classes.messageRow}>
                      <Typography className={classes.greyTextSmall}>
                        Lima: entre 24 a 48 horas
                      </Typography>
                      <Typography className={classes.greyTextSmall} paragraph>
                        {' | '}
                        Provincia: 3 - 4 días útiles
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            </Grid>

            <Grid item xs={12} md={12}>
              <CustomButton
                variant="contained"
                type="submit"
                disabled={isSaving}
                fullWidth
                className={classes.submitButton}
              >
                Crear Zona de envío
              </CustomButton>
            </Grid>
          </Grid>

          <div className={classes.summaryContainerGap}>
            <div className={classes.summaryContainer}>
              <Typography className={classes.subtitleText}>
                Resumen de Envío
              </Typography>
              <Divider className={classes.divider} />
              <div className={classes.containerSteps}>
                {/* <div>
                  <Typography className={classes.subtitleText2}>
                    {country.name}
                  </Typography>
                  <Typography className={classes.greyTextSmall}>
                    Pedido Mínimo para delivery: s/ 99 soles
                  </Typography>
                </div> */}
                {shippingRatesWatch?.length > 0 && (
                  <div>
                    <Typography className={classes.subtitleText2}>
                      Tarifas
                    </Typography>
                    <Typography className={classes.greyTextSmall}>
                      {shippingRatesWatch?.map((rate, index, array) => {
                        return `${rate.name}${
                          index < array.length - 1 ? ' - ' : ''
                        }`;
                      })}
                    </Typography>
                  </div>
                )}
                {Object.values(locationsSelected).reduce(
                  (acc, curr) => acc.concat(curr),
                  []
                ).length > 0 && (
                  <div>
                    <Typography className={classes.subtitleText2}>
                      Localidades
                    </Typography>
                    <Typography className={classes.greyTextSmallContainer}>
                      {Object.values(locationsSelected)
                        .reduce((acc, curr) => acc.concat(curr), [])
                        .join(', ')}
                    </Typography>
                  </div>
                )}
              </div>
            </div>

            {/* <div className={classes.wrapperAction}>
          {!hasShippingMethodFeatureDisabled && (
            <CustomButton
              variant="contained"
              onClick={handleSubmit(submitForm)}
            >
              Confirmar Tarifa
            </CustomButton>
          )}

          {adminRate && !hasShippingMethodFeatureDisabled && (
            <CustomButton
              className={classes.deleteRate}
              onClick={handlePromptOpen}
            >
              Eliminar tarifa
            </CustomButton>
          )}
        </div> */}
          </div>
        </form>
      )}

      {adminRateId && shippingAdvanced && (
        <form
          className={classes.containerGrid}
          onSubmit={handleSubmit(handleBeforeSubmit)}
        >
          <Grid container spacing={1} className={classes.containerGridSteps}>
            <Grid item xs={12} md={12}>
              <Typography className={classes.subtitleText}>
                1. Título de Zona de Reparto
              </Typography>
              <CustomTextField
                name="label"
                label="Título"
                defaultValue={shippingAdvanced ? shippingAdvanced.label : ''}
                error={!!errors.label}
                helperText={errors.label ? errors.label.message : ''}
                type="text"
                inputRef={register({required: 'Campo obligatorio'})}
                disabled={isSaving}
              />
            </Grid>
            {/* <Grid item xs={12} md={12}>
          <Typography className={classes.subtitleText}>
            2. Pedido mínimo para delivery
          </Typography>
          <CustomTextField
            name="price"
            label="Precio de pedido"
            defaultValue={adminRate ? adminRate.price : ''}
            InputProps={{
              className: classes.textInput,
              startAdornment: (
                <InputAdornment position="start">
                  {currency ? currency.symbol : ''}
                </InputAdornment>
              ),
            }}
            inputRef={register({
              required: 'Campo obligatorio',
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: 'Ingrese un monto válido',
              },
            })}
            error={!!errors.price}
            helperText={errors.price ? errors.price.message : ''}
            // disabled={isSaving }
          />
        </Grid> */}
            <Divider className={classes.dividerNoMargin} />
            <Grid item xs={12} md={12}>
              <Typography className={classes.subtitleText}>
                2. Crear tarifa(s)
              </Typography>
              <Grid container spacing={3}>
                {/* {shippingAdvanced.shippingRates.map((shippingRate, index) => ( */}
                {newRates.map((shippingRate, index) => (
                  <Grid item xs={12} md={6} key={shippingRate.id}>
                    <div className={classes.newRateCard}>
                      <CustomTextField
                        name={`shippingRates[${index}].id`}
                        type="hidden"
                        inputRef={register}
                        defaultValue={shippingRate.id}
                      />
                      <CustomTextField
                        name={`shippingRates[${index}].name`}
                        label="Nombre de tarifa"
                        defaultValue={shippingRate ? shippingRate.name : ''}
                        inputRef={register({required: 'Campo obligatorio'})}
                        error={!!errors.name}
                        helperText={errors.name ? errors.name.message : ''}
                        type="text"
                        disabled={isSaving}
                      />
                      <CustomTextField
                        name={`shippingRates[${index}].price`}
                        label="Precio de pedido"
                        defaultValue={shippingRate ? shippingRate.price : ''}
                        InputProps={{
                          className: classes.textInput,
                          startAdornment: (
                            <InputAdornment position="start">
                              {currency ? currency.symbol : ''}
                            </InputAdornment>
                          ),
                        }}
                        inputRef={register({
                          required: 'Campo obligatorio',
                          pattern: {
                            value: /^\d+(\.\d{1,2})?$/,
                            message: 'Ingrese un monto válido',
                          },
                        })}
                        error={!!errors.price}
                        helperText={errors.price ? errors.price.message : ''}
                        disabled={isSaving}
                      />
                      <CustomTextField
                        name={`shippingRates[${index}].description`}
                        label="Descripción (Opcional)"
                        inputRef={register()}
                        disabled={isSaving}
                        defaultValue={
                          shippingRate ? shippingRate.description : ''
                        }
                      />
                      <CustomButton
                        fullWidth
                        className={classes.deleteRateButton}
                        onClick={() => {
                          remove(index);
                          setNewRates(prev => {
                            const updatedRates = [...prev];
                            updatedRates.splice(index, 1);
                            return updatedRates;
                          });
                        }}
                      >
                        Eliminar tarifa
                      </CustomButton>
                    </div>
                  </Grid>
                ))}
              </Grid>
              {limitNewRates > newRates.length && (
                <CustomButton
                  fullWidth
                  className={classes.newRateButton}
                  onClick={() =>
                    setNewRates(
                      newRates.concat({
                        id: newRates.length + 1,
                        name: '',
                        price: '',
                        description: '',
                      })
                    )
                  }
                  // disabled={hasShippingMethodFeatureDisabled}
                >
                  <div className={classes.wrapperAddBtn}>
                    <Icon size="small">
                      <AddIcon className={classes.iconAdd} />
                    </Icon>
                    <Typography className={classes.textButton}>
                      Agregar nueva
                    </Typography>
                  </div>
                </CustomButton>
              )}
            </Grid>
            <Divider className={classes.dividerNoMargin} />
            <Grid item xs={12} md={12}>
              <Typography className={classes.subtitleText}>
                3. Elegir localidades
              </Typography>
              {availableShippingCountries &&
                availableShippingCountries.length > 0 &&
                availableShippingCountries
                  .filter(countryI => countryI.code !== country.code)
                  .map(country => {
                    return (
                      <LocationSelector
                        key={`country-${country.code}`}
                        country={country}
                        defaultSelectedAdmins={shippingAdvanced.location}
                        setLocationsSelected={selected => {
                          setLocationsSelected(prevState => ({
                            ...prevState,
                            [country.code]: selected,
                          }));
                        }}
                        locationsSelected={
                          locationsSelected[country.code] || []
                        }
                      />
                    );
                  })}
            </Grid>
            <Divider className={classes.dividerNoMargin} />
            <Grid item xs={12} md={12}>
              <Typography className={classes.subtitleText}>
                4. Mensaje de envio
              </Typography>
              <CustomTextField
                multiline
                rows={5}
                rowsMax={18}
                label="Mensaje"
                className={classes.messageField}
                InputProps={{
                  className: classes.textInput,
                }}
                InputLabelProps={{className: classes.textInputLabel}}
                name="description"
                error={!!errors.description}
                helperText={errors.description ? errors.description : ''}
                defaultValue={
                  shippingAdvanced ? shippingAdvanced.description : ''
                }
                type="text"
                inputRef={register}
                disabled={isSaving}
              />
              <div className={classes.messageTips}>
                <Typography className={classes.greyTextSmall} paragraph>
                  Es importante incluir información como:
                </Typography>
                <div className={classes.messageTipsRow}>
                  <div>
                    <Typography
                      className={`${classes.greyTextSmall} ${classes.bold}`}
                    >
                      Días de despacho
                    </Typography>
                    <Typography className={classes.greyTextSmall} paragraph>
                      Despachos los martes y jueves
                    </Typography>
                  </div>
                  <Divider orientation="vertical" flexItem />
                  <div>
                    <Typography
                      className={`${classes.greyTextSmall} ${classes.bold}`}
                    >
                      Rango de tiempo hasta la recepción del pedido:
                    </Typography>
                    <div className={classes.messageRow}>
                      <Typography className={classes.greyTextSmall}>
                        Lima: entre 24 a 48 horas
                      </Typography>
                      <Typography className={classes.greyTextSmall} paragraph>
                        {' | '}
                        Provincia: 3 - 4 días útiles
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} md={12}>
              <CustomButton
                variant="contained"
                type="submit"
                className={classes.submitButton}
                disabled={isSaving}
                fullWidth
              >
                Crear Zona de envío
              </CustomButton>
            </Grid>
          </Grid>

          <div className={classes.summaryContainerGap}>
            <div className={classes.summaryContainer}>
              <Typography className={classes.subtitleText}>
                Resumen de Envío
              </Typography>
              <Divider className={classes.divider} />
              <div className={classes.containerSteps}>
                {/* <div>
                  <Typography className={classes.subtitleText2}>
                    {country.name}
                  </Typography>
                  <Typography className={classes.greyTextSmall}>
                    Pedido Mínimo para delivery: s/ 99 soles
                  </Typography>
                </div> */}
                {shippingRatesWatch?.length > 0 && (
                  <div>
                    <Typography className={classes.subtitleText2}>
                      Tarifas
                    </Typography>
                    <Typography className={classes.greyTextSmall}>
                      {shippingRatesWatch?.map((rate, index, array) => {
                        return `${rate.name}${
                          index < array.length - 1 ? ' - ' : ''
                        }`;
                      })}
                    </Typography>
                  </div>
                )}
                {Object.values(locationsSelected).reduce(
                  (acc, curr) => acc.concat(curr),
                  []
                ).length > 0 && (
                  <div>
                    <Typography className={classes.subtitleText2}>
                      Localidades
                    </Typography>
                    <Typography className={classes.greyTextSmallContainer}>
                      {Object.values(locationsSelected)
                        .reduce((acc, curr) => acc.concat(curr), [])
                        .join(', ')}
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      )}

      <ActivateShippingModal
        open={showModalActive}
        handleClose={() => {
          setShowModalActive(false);
        }}
        typeRateSelected="ADVANCED"
        handleFormSubmit={handleFormSubmit}
        isSaving={isSaving}
      />
    </>
  );
}
